import { useState } from 'react';
import {
  aiPauseSet,
  aiPauseState,
  atlasAutonomy,
  atlasAutonomySet,
  killSwitchSet,
  killSwitchState,
  type OversightAiPause,
  type AtlasAutonomyLevel,
  type AtlasAutonomyResponse,
  type AtlasAutonomyRung,
  type KillSwitchState,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useFacilityScope, usePickedIds } from '../../session/FacilityScope';
import { useQuery } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { ErrorState, Skeleton } from '../../ui/states';
import { ErrorNote } from '../../ErrorNote';
import { AboutButton } from '../../ui/AboutThis';
import { noDash, stop, type AtlasRole } from './common';
import { AutonomyProposals, PlatformGovernance } from './AutonomyGovernance';
import type { ApiError } from '@aithora/shared';

const ORDER: AtlasAutonomyLevel[] = ['observe', 'suggest', 'assist', 'full'];

/** The short name: served, or the ladder label without its 'L1 · ' prefix. */
const whatOf = (r: AtlasAutonomyRung) => r.short ?? noDash(r.label.replace(/^L\d\s*·\s*/, ''));
/** The signature line: served, or what the level does on the server today. */
function sigOf(r: AtlasAutonomyRung): string {
  if (r.signature) return r.signature;
  switch (r.level) {
    case 'observe':
      return 'no action taken';
    case 'suggest':
      return '1 signature before anything runs';
    case 'assist':
      return 'low-risk runs on its own · the rest needs 1 signature';
    default:
      return 'no signature · hard floors still apply';
  }
}

/**
 * Autonomy (design: atIsAuto, T13486): how far Atlas may go on its own, and the freeze.
 * Both live on the page, never in the slide-out. FULL is two-person gated on the server
 * (PUT answers 202 pending a second operator); the design keeps it locked here.
 */
export function AutonomyTab({ ar }: { ar: AtlasRole }) {
  const { client, session } = useSession();
  const roReason = useReadOnlyReason();
  const orgId = session?.user.organizationId ?? null;
  const isFA = ar === 'Facility Admin';
  // Autonomy is per building. The team sets the one building picked in the switcher, or with
  // none picked the company default every building without its own level follows.
  const picked = usePickedIds();
  const oneBuilding = ar === 'Operator' && picked?.length === 1 ? picked[0] : null;
  const pickedName = useFacilityScope().inView.find((f) => f.id === oneBuilding)?.name ?? 'this building';
  const forWhom = oneBuilding ? { facilityId: oneBuilding } : {};
  const auto = useQuery<AtlasAutonomyResponse>((signal) => atlasAutonomy(client, { query: forWhom, signal }), [client, oneBuilding]);
  const killScope = isFA ? 'this building' : 'every facility';
  const hasKill = ar === 'Facility Admin' || ar === 'Operator';
  const canArm = ar === 'Facility Admin' || ar === 'Operator';
  const pause = useQuery<OversightAiPause>((signal) => aiPauseState(client, { params: { id: orgId! }, signal }), [client, orgId], { enabled: isFA && !!orgId });
  const kill = useQuery<KillSwitchState>((signal) => killSwitchState(client, { signal }), [client], { enabled: ar === 'Operator' });
  const [setErr, setSetErr] = useState<ApiError | null>(null);
  const [confirm, setConfirm] = useState<'freeze' | 'resume' | null>(null);
  // FULL is two-person: asking for it opens a request (with a reason) the owner approves.
  const [askFull, setAskFull] = useState(false);
  const [about, setAbout] = useState(false);
  const [fullNote, setFullNote] = useState<string | null>(null);
  const [govKey, setGovKey] = useState(0);

  const view = auto.data?.autonomy ?? null;
  const level = view?.level ?? null;
  const ladder = view ? [...view.ladder].sort((a, b) => ORDER.indexOf(a.level) - ORDER.indexOf(b.level)) : [];
  const cur = ladder.find((r) => r.level === level) ?? null;
  const locked = (r: AtlasAutonomyRung) => (r.level === 'full' && level !== 'full') || !!r.disabledByCeiling;
  const killOn = isFA ? !!pause.data?.paused : !!kill.data?.frozen && kill.data.scope === 'global';

  const setLevel = async (lv: AtlasAutonomyLevel) => {
    if (roReason || lv === level) return;
    setSetErr(null);
    try {
      const r = await atlasAutonomySet(client, { body: { level: lv, ...forWhom } });
      if (r.autonomy) auto.setData((d) => (d ? { ...d, autonomy: { ...d.autonomy, ...r.autonomy!, ladder: d.autonomy.ladder } } : d));
      else auto.reload();
    } catch (e) {
      setSetErr(e as ApiError);
    }
  };

  const doFreeze = async (on: boolean) => {
    if (isFA) {
      const r = await aiPauseSet(client, { params: { id: orgId! }, body: { paused: on, confirm: true } });
      pause.setData(() => r);
    } else {
      await killSwitchSet(client, { body: { action: on ? 'engage' : 'release', scope: 'global', confirm: true } });
      kill.reload();
    }
  };

  return (
    <div data-testid="atlas-autonomy" style={sx('flex:1 1 auto;min-height:0;min-width:0;overflow-y:auto;display:flex;flex-direction:column;')}>
      {/* V9: the explainer opens on the "i" after the lede (design k14895). */}
      <div style={sx('font-size:19px;font-weight:700;letter-spacing:-.022em;color:#22272E;padding-bottom:2px;')}>
        {cur ? cur.level.toUpperCase() + ' · ' + whatOf(cur) : ''}
        <AboutButton open={about} onToggle={() => setAbout((o) => !o)} gap={6} />
      </div>
      {about && (
        <div style={sx('font-size:16px;color:#3C454F;line-height:1.55;max-width:84ch;text-wrap:pretty;padding-bottom:13px;')}>
          How far Atlas may go on its own. This lives on the page and never in the slide-out, because you should not be able to arm autonomy from something you might dismiss by accident.
        </div>
      )}

      {auto.loading && !auto.data && <Skeleton rows={4} label="Loading the autonomy level" />}
      {auto.error && <ErrorState error={auto.error} onRetry={auto.reload} />}

      {ladder.map((r) => {
        const on = r.level === level;
        const lk = !on && locked(r);
        return (
          <div key={r.level} data-testid="autonomy-rung" style={sx('min-width:0;padding:12px 0;border-top:1px solid rgba(34,39,46,.14);' + (lk ? 'opacity:.65;' : ''))}>
            <div style={sx('display:flex;align-items:baseline;gap:11px;flex-wrap:wrap;row-gap:5px;min-width:0;')}>
              <span
                style={sx(
                  'flex:0 0 auto;display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:' +
                    (on ? '#FCFAF4' : '#54606C') +
                    ';background:' +
                    (on ? '#2F5C97' : 'transparent') +
                    ';' +
                    (on ? '' : lk ? 'border:1px dashed rgba(34,39,46,.22);' : 'width:0;padding:0;'),
                )}
              >
                {/* the design's value span: an empty mark still holds the row's baseline */}
                <span>{on ? 'NOW' : lk ? 'LOCKED' : ''}</span>
              </span>
              <span style={sx('flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.07em;color:' + (on ? '#1D3F6B' : lk ? '#54606C' : '#3C454F') + ';')}>{r.level.toUpperCase()}</span>
              <span style={sx('flex:0 0 auto;font-size:16px;font-weight:700;letter-spacing:-.012em;color:#22272E;')}>{whatOf(r)}</span>
              <span style={sx('flex:1 1 160px;min-width:0;font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{sigOf(r)}</span>
            </div>
            <div style={sx('font-size:16px;color:#3C454F;line-height:1.55;margin-top:4px;max-width:88ch;text-wrap:pretty;')}>{noDash(r.desc)}</div>
          </div>
        );
      })}

      {canArm && view && (
        <div style={sx('min-width:0;padding:13px 0 0;border-top:1px solid rgba(34,39,46,.14);margin-top:13px;')}>
          <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;')}>Set the level</div>
          <div data-testid="autonomy-applies" style={sx('font-size:16px;color:#3C454F;line-height:1.5;margin-top:4px;')}>
            {isFA
              ? 'Applies to this building only.'
              : oneBuilding
                ? `Applies to ${pickedName} only.${auto.data?.ownLevel ? '' : ' It follows the company default until you set it.'}`
                : 'The company default: every building that has not set its own level follows it. Pick one building to set just that one.'}
          </div>
          <div role="radiogroup" aria-label="Autonomy level" style={sx('display:flex;gap:5px;margin-top:9px;min-width:0;flex-wrap:wrap;')}>
            {ladder.map((r) => {
              const on = r.level === level;
              const lk = !on && locked(r);
              return (
                <a
                  key={r.level}
                  href="#"
                  role="radio"
                  aria-checked={on}
                  aria-disabled={lk || !!roReason || undefined}
                  title={roReason ?? (lk ? (r.disabledByCeiling ? 'Above the Aithora ceiling.' : 'FULL needs two distinct operators. Click to request it.') : undefined)}
                  onClick={(e) => {
                    stop(e);
                    if (roReason) return;
                    if (!lk) void setLevel(r.level);
                    else if (r.level === 'full' && !r.disabledByCeiling) setAskFull(true);
                  }}
                  style={sx(
                    'flex:1;display:inline-flex;align-items:center;justify-content:center;height:30px;border-radius:7px;font-size:14px;font-weight:700;letter-spacing:.06em;text-decoration:none;' +
                      (on
                        ? 'background:#2F5C97;color:#fff;'
                        : lk
                          ? 'background:rgba(34,39,46,.04);color:#54606C;border:1px dashed rgba(34,39,46,.3);cursor:default;'
                          : 'background:transparent;color:#54606C;border:1px solid rgba(34,39,46,.14);'),
                  )}
                >
                  {r.level.toUpperCase()}
                </a>
              );
            })}
          </div>
          <div style={sx('font-size:16px;color:#3C454F;line-height:1.55;margin-top:10px;max-width:86ch;text-wrap:pretty;')}>
            FULL needs two distinct operators. Requesting it returns a pending approval rather than switching, per the two-person rule. Clinical, emergency and Atlas directive actions are hard-floored and never auto-execute at any level.
          </div>
          {fullNote && <div style={sx('font-size:16px;color:#1F6B52;margin-top:6px;')}>{fullNote}</div>}
          {setErr && <ErrorNote error={setErr} compact />}
          <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:7px;margin-top:10px;min-width:0;')} />
        </div>
      )}

      {canArm && <AutonomyProposals key={'p' + govKey} onChanged={() => auto.reload()} />}

      {hasKill && (
        <div data-testid="atlas-freeze" style={sx('min-width:0;padding:13px 0 2px;border-top:1px solid rgba(34,39,46,.14);margin-top:14px;')}>
          <div style={sx('font-size:16px;font-weight:700;letter-spacing:-.012em;color:#22272E;')}>{killOn ? 'Frozen · ' + killScope : 'Would freeze ' + killScope}</div>
          <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;margin-top:6px;min-width:0;')}>
            <a
              href="#"
              aria-disabled={!!roReason || undefined}
              title={roReason ?? (killOn && !isFA ? 'Only the owner turns the AI back on. The server checks.' : undefined)}
              onClick={(e) => {
                stop(e);
                if (!roReason) setConfirm(killOn ? 'resume' : 'freeze');
              }}
              style={sx(
                'display:inline-flex;align-items:center;height:32px;padding:0 14px;margin-top:9px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:' +
                  (killOn ? '#12503C' : '#FCFAF4') +
                  ';background:' +
                  (killOn ? 'rgba(47,138,111,.14)' : '#B3261E') +
                  ';',
              )}
            >
              {killOn ? 'Resume' : 'Freeze ' + killScope}
            </a>
            <span style={sx('flex:1 1 220px;min-width:0;font-size:16px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>
              {killOn
                ? 'Atlas is stopped. Safety escalation and 911 stay live. Sealed to the ledger under your name.'
                : 'Atlas stops. Safety escalation and 911 stay live. This is page-only and never available from a panel you might dismiss.'}
            </span>
          </div>
        </div>
      )}

      {ar === 'Operator' && <PlatformGovernance onChanged={() => { auto.reload(); setGovKey((k) => k + 1); }} />}

      {askFull && (
        <ConfirmDialog
          title="Request FULL autonomy?"
          body="Nothing changes yet. The request waits for the owner to approve it; only then may Atlas act on the dialable, low-risk actions with no signature. Clinical, emergency and Atlas directive actions always need a person."
          confirmLabel="Request FULL"
          reason={{ label: 'Why', placeholder: 'Why FULL is safe here (required)', required: true }}
          onConfirm={async (why) => {
            setSetErr(null);
            const r = await atlasAutonomySet(client, { body: { level: 'full', reason: why, ...forWhom } });
            setFullNote(r.pendingApproval ? 'Requested. It takes effect once the owner approves it.' : null);
            auto.reload();
            setGovKey((k) => k + 1);
          }}
          onClose={() => setAskFull(false)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm === 'freeze' ? 'Freeze ' + killScope + '?' : 'Resume Atlas?'}
          body={
            confirm === 'freeze'
              ? 'Atlas stops answering in ' + killScope + '. Safety escalation and 911 stay live. It is sealed to the Truth Ledger under your name.'
              : 'Atlas starts answering again. It is sealed to the Truth Ledger under your name.'
          }
          confirmLabel={confirm === 'freeze' ? 'Freeze' : 'Resume'}
          danger={confirm === 'freeze'}
          onConfirm={() => doFreeze(confirm === 'freeze')}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
